import React, { useEffect, useMemo, useState } from 'react';

import { CircularProgress } from '@material-ui/core';
import { FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { successAlert, errorAlert, getStation, editStation } from 'actions';
import { NotAuthorized } from 'app/components/not-authorized';
import { updateStationCodeApi } from 'app/operations/stations/station-api';
import { StationForm } from 'app/operations/stations/station-form';
import { StationFormState } from 'app/operations/stations/station-helpers';
import { trimTrailingSlash, useTypedSelector } from 'helpers';
import { canEditOperationsSubSystemEntity } from 'selectors';
import { Station } from 'types';

const EditStation: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch<{ id: string }>();
  const baseURL = trimTrailingSlash(match.url);
  const [isLoading, setLoading] = useState(true);
  const intl = useIntl();

  const stationId = match.params.id;

  const stationData = useTypedSelector(
    state => state.stations.collection[stationId] as Station | undefined
  );

  useEffect(() => {
    if (stationData) {
      setLoading(false);
    } else {
      dispatch(getStation(stationId));
    }
  }, [dispatch, stationData, stationId]);

  const initialState: StationFormState = useMemo(
    () => ({
      en_name: stationData?.en_name || '',
      ar_name: stationData?.ar_name || '',
      station_code: stationData?.station_code || '',
      organisationId: stationData?.organisation_id || '',
      organisation: stationData?.organisation || undefined,
      enabled: !stationData?.disabled,
    }),
    [stationData]
  );

  const hasWriteAccessToCurrentStation = useTypedSelector(state =>
    canEditOperationsSubSystemEntity(state, 'Stations', stationId)
  );

  const handleSubmit = (
    values: StationFormState,
    { setSubmitting }: FormikHelpers<StationFormState>
  ) => {
    setSubmitting(true);

    Promise.resolve(
      dispatch(
        editStation(stationId, {
          en_name: values.en_name,
          ar_name: values.ar_name,
          organisation_id: values.organisationId,
          disabled: !values.enabled,
        })
      )
    )
      .then(async () => {
        if (values.station_code !== (stationData?.station_code || '')) {
          try {
            await updateStationCodeApi(stationId, values.station_code);
          } catch (err: any) {
            const message =
              err?.response?.data?.message ||
              intl.formatMessage({ id: 'stationCodeUpdateFailed' });

            dispatch(errorAlert(message));

            return;
          }
        }

        history.push(`${baseURL}`.replace('edit', ''));

        dispatch(
          successAlert(intl.formatMessage({ id: 'successEditStation' }))
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          height: 400,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!hasWriteAccessToCurrentStation) {
    return <NotAuthorized />;
  }

  return <StationForm {...{ initialState, handleSubmit, isEditForm: true }} />;
};

export { EditStation };
